import { dollar, fixedPercent } from "../../node_modules/@zillow/formatters/dist/formatters.esm.esm.js";
import API from "@zillow/mortgage-api-client";
import { PAGE_FLAVOR_LOAN_TYPE_MAP, LOAN_TYPES, DEFAULT_PARTNER_ID } from "../../utils/mortgagehelpers/constants.esm.js";
import MortgagePageClassification from "../../utils/mortgagehelpers/MortgagePageClassification.esm.js";
import { createMax } from "../../utils/mortgagehelpers/mortgage-helpers.esm.js";
const BRANDING = {
  ZGMI_INC: "Zillow Group Marketplace, Inc.",
  ZGMI_FULL_NAME: 'Zillow Group Marketplace, Inc. ("ZGMI")',
  ZGMI_NMLS_ID: "NMLS #1303160"
};
function formatIntoDollars(value, cents, parentheses) {
  let s = dollar(value, false);
  return s;
}
function formatIntoPercent(value, fixed, decimalPlaces) {
  return fixedPercent(value, decimalPlaces);
}
const PROGRAM_KEYS = {
  Fixed30Year: "Fixed30Year",
  Fixed20Year: "Fixed20Year",
  Fixed15Year: "Fixed15Year",
  Fixed10Year: "Fixed10Year",
  ARM7: "ARM7",
  ARM5: "ARM5",
  ARM3: "ARM3"
};
const PROGRAM_NAMES = {
  [PROGRAM_KEYS.Fixed30Year]: "30-Year Fixed Rate",
  [PROGRAM_KEYS.Fixed20Year]: "20-Year Fixed Rate",
  [PROGRAM_KEYS.Fixed15Year]: "15-Year Fixed Rate",
  [PROGRAM_KEYS.Fixed10Year]: "10-Year Fixed Rate",
  [PROGRAM_KEYS.ARM7]: "7-year ARM",
  [PROGRAM_KEYS.ARM5]: "5-year ARM",
  [PROGRAM_KEYS.ARM3]: "3-year ARM"
};
const {
  Fixed30Year,
  Fixed20Year,
  Fixed15Year,
  Fixed10Year,
  ARM7,
  ARM5,
  ARM3
} = PROGRAM_KEYS;
const DEFAULT_LOAN_PROGRAMS = [Fixed30Year, Fixed15Year, ARM5];
const CONFORMING_LOAN_PROGRAMS = [Fixed30Year, Fixed20Year, Fixed15Year, Fixed10Year, ARM7, ARM5, ARM3];
const getLegalDisclosureText = (todayPayment, today, name, isRefinance) => {
  const loanAmount = formatIntoDollars(todayPayment.loanAmount);
  const monthlyPayment = formatIntoDollars(todayPayment.monthlyPayment);
  const apr = formatIntoPercent(today.apr, true, 2);
  if (isRefinance) {
    const homeValue = formatIntoDollars(todayPayment.loanAmount / (todayPayment.loanToValue / 100));
    return `A ${name} loan of ${loanAmount} at ${apr} APR with an estimated home value of ${homeValue} will have a monthly payment of ${monthlyPayment}.`;
  } else {
    const downPayment = formatIntoDollars(todayPayment.downPayment);
    return `A ${name} loan of ${loanAmount} at ${apr} APR with a down payment of ${downPayment} will have a monthly payment of ${monthlyPayment}.`;
  }
};
const getNationalConformingLoanLimits = async () => {
  createMax();
  try {
    const limits = await API.MAX.GET({
      action: "getNationalConformingLoanLimits",
      input: {
        partnerId: API.partnerId ? API.partnerId : DEFAULT_PARTNER_ID
      }
    });
    return limits;
  } catch (e) {
    return {};
  }
};
const getRateTablesApi = async ({
  queriesOverride,
  loanPrograms,
  stateAbbreviation,
  refinance,
  loanType,
  loanAmountBucket,
  loanToValueBucket,
  creditScoreBucket
}) => {
  let queries = queriesOverride;
  createMax();
  if (!queries) {
    queries = loanPrograms.reduce((acc, loanProgram) => ({
      ...acc,
      [loanProgram]: {
        program: loanProgram,
        stateAbbreviation,
        refinance,
        loanType,
        loanAmountBucket,
        loanToValueBucket,
        creditScoreBucket
      }
    }), {});
  }
  return await API.MAX.GET({
    action: "getRateTables",
    input: {
      partnerId: API.partnerId ? API.partnerId : DEFAULT_PARTNER_ID,
      queries
    }
  }).then((response) => Object.entries(response.rates).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: {
      id: key,
      // required a GUID for iterating table data,
      ...value,
      name: PROGRAM_NAMES[key] ?? key
      // required to display program name
    }
  }), {}));
};
const getMortgageRates = async (pathname) => {
  const classification = new MortgagePageClassification(pathname ? pathname : "/mortgage-rates/");
  const {
    stateAbbreviation,
    flavor,
    refinance,
    city
  } = classification;
  const loanType = PAGE_FLAVOR_LOAN_TYPE_MAP[flavor] ?? LOAN_TYPES.Conventional;
  const isRefinance = refinance;
  let data = {};
  const inputs = [{
    queriesOverride: null,
    refinance: isRefinance,
    loanPrograms: CONFORMING_LOAN_PROGRAMS
  }, {
    queriesOverride: governmentQueries(isRefinance, stateAbbreviation),
    refinance: false,
    loanPrograms: DEFAULT_LOAN_PROGRAMS
  }, {
    queriesOverride: jumboQueries(isRefinance, stateAbbreviation, city),
    refinance: false,
    loanPrograms: DEFAULT_LOAN_PROGRAMS
  }];
  try {
    const getData = async () => {
      for (let i = 0; i < inputs.length; i++) {
        const response = await getRateTablesApi({
          ...DEFAULT_RATE_API_PARAMS,
          loanPrograms: inputs[i].loanPrograms,
          queriesOverride: inputs[i].queriesOverride,
          stateAbbreviation,
          refinance: inputs[i].refinance,
          loanType
        });
        data = {
          ...data,
          ...response
        };
      }
      return data;
    };
    const result = await getData();
    return result;
  } catch (error) {
    console.error("Error fetching data:", error);
    return data;
  }
};
const {
  FHA,
  VA
} = LOAN_TYPES;
const LOAN_TO_VALUE_BUCKET = {
  Normal: "Normal",
  VeryHigh: "VeryHigh"
};
const CREDIT_SCORE_BUCKET = {
  High: "High",
  VeryHigh: "VeryHigh"
};
const DEFAULT_RATE_API_PARAMS = {
  loanType: LOAN_TYPES.Conventional,
  loanAmountBucket: "Conforming",
  loanToValueBucket: LOAN_TO_VALUE_BUCKET.Normal,
  creditScoreBucket: CREDIT_SCORE_BUCKET.VeryHigh
};
const governmentQueries = (refinance, stateAbbreviation) => {
  const common = {
    refinance: !!refinance,
    stateAbbreviation,
    loanToValueBucket: LOAN_TO_VALUE_BUCKET.VeryHigh,
    creditScoreBucket: CREDIT_SCORE_BUCKET.High
  };
  return {
    "30-Year Fixed Rate FHA": {
      ...common,
      program: PROGRAM_KEYS.Fixed30Year,
      loanType: FHA
    },
    "30-Year Fixed Rate VA": {
      ...common,
      program: PROGRAM_KEYS.Fixed30Year,
      loanType: VA
    },
    "15-Year Fixed Rate FHA": {
      ...common,
      program: PROGRAM_KEYS.Fixed15Year,
      loanType: FHA
    },
    "15-Year Fixed Rate VA": {
      ...common,
      program: PROGRAM_KEYS.Fixed15Year,
      loanType: VA
    }
  };
};
const LOAN_AMOUNT_BUCKETS = {
  Jumbo: "Jumbo"
};
const {
  Jumbo
} = LOAN_AMOUNT_BUCKETS;
const jumboQueries = (refinance, stateAbbreviation, cityName) => {
  const common = {
    loanAmountBucket: Jumbo,
    refinance: !!refinance,
    stateAbbreviation,
    paymentSummaryOptions: {
      stateAbbreviation,
      cityName
    }
  };
  return {
    "30-Year Fixed Rate Jumbo": {
      ...common,
      program: Fixed30Year
    },
    "15-Year Fixed Rate Jumbo": {
      ...common,
      program: Fixed15Year
    },
    "7-year ARM Jumbo": {
      ...common,
      program: ARM7
    },
    "5-year ARM Jumbo": {
      ...common,
      program: ARM5
    },
    "3-year ARM Jumbo": {
      ...common,
      program: ARM3
    }
  };
};
export {
  BRANDING,
  CREDIT_SCORE_BUCKET,
  DEFAULT_RATE_API_PARAMS,
  Jumbo,
  LOAN_AMOUNT_BUCKETS,
  LOAN_TO_VALUE_BUCKET,
  PROGRAM_KEYS,
  PROGRAM_NAMES,
  formatIntoDollars,
  formatIntoPercent,
  getLegalDisclosureText,
  getMortgageRates,
  getNationalConformingLoanLimits,
  getRateTablesApi,
  governmentQueries,
  jumboQueries
};
